import * as React from "react";
import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import throttle from "lodash/throttle";
import * as Yup from "yup";
import { debounce } from "lodash";
import { useGetLocalAndLenderErrors } from "@/hooks/useRequiredFields/useGetLocalAndLenderErrors";
import regex from "../../utils/regex";

function loadScript(src, position, id) {
  if (!position) {
    return;
  }

  const script = document.createElement("script");
  script.setAttribute("async", "");
  script.setAttribute("id", id);
  script.src = src;
  position.appendChild(script);
}

const autocompleteService = { current: null };
let placesService = null;
let map = null;

function GoogleMaps({
  handleCreateAddress,
  handleRemoveAddress,
  handleUpdateAddress,
  setGuarantorFieldErrors,
  address,
  isEmptyAddress,
  isEntity,
  customerId,
  disabled,
  style,
  addressFieldName,
  validateComponentField,
  canRemoveAddress = true,
  requiredFieldName = "",
}) {
  const [value, setValue] = useState(address?.fullAddress || address || "");
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const loaded = useRef(false);

  if (typeof window !== "undefined" && !loaded.current) {
    if (!document.querySelector("#google-maps")) {
      loadScript(
        "https://maps.googleapis.com/maps/api/js?key=AIzaSyByJ8mHpDMsQYWH-ToZE5eLBvEouildEYs&libraries=places&loading=async",
        document.querySelector("head"),
        "google-maps",
      );
    }

    loaded.current = true;
  }

  useEffect(() => {
    if (window.google?.maps?.Map) {
      map = new window.google.maps.Map(document.querySelector("#google-maps"), {
        center: { lat: 0, lng: 0 },
        zoom: 15,
      });
    }
  }, [window.google?.maps?.Map]);

  const fetch = useMemo(
    () =>
      throttle((request, callback) => {
        autocompleteService.current.getPlacePredictions(request, callback);
      }, 200),
    [],
  );

  useEffect(() => {
    if (inputValue?.length <= 200) {
      let active = true;

      if (!autocompleteService.current && window.google?.maps?.places) {
        autocompleteService.current = new window.google.maps.places.AutocompleteService();
      }

      if (!placesService && map && window.google?.maps?.places) {
        placesService = new window.google.maps.places.PlacesService(map);
      }

      if (!placesService) {
        return undefined;
      }

      if (!autocompleteService.current) {
        return undefined;
      }

      if (inputValue === "") {
        setOptions(value ? [value] : []);
        return undefined;
      }

      fetch({ input: inputValue, componentRestrictions: { country: "au" } }, (results) => {
        if (active) {
          let newOptions = [];

          if (value) {
            newOptions = [value];
          }

          if (results) {
            newOptions = [...newOptions, ...results];
          }

          setOptions(newOptions);
        }
      });

      return () => {
        active = false;
      };
    }
  }, [value, inputValue, fetch]);

  const [fieldErrors, setFieldErrors] = useState({
    fullAddress: "",
  });

  const validationSchema = Yup.object({
    fullAddress: Yup.string()
      .required("address field is required")
      .min(3, "Minimum of 3 characters")
      .max(200, "Maximum of 200 characters"),
    entityAddress: Yup.string().optional(),
  });

  const debouncedValidation = useCallback(
    debounce(async (fieldName, value) => {
      try {
        if (validateComponentField !== undefined) {
          await validateComponentField(fieldName, value);
        }
        const name = fieldName.split("_")[0];
        if (isEntity) {
          setGuarantorFieldErrors((prevErrors) => ({
            ...prevErrors,
            [fieldName]: "",
          }));
        } else {
          setGuarantorFieldErrors((prevErrors) => ({
            ...prevErrors,
            [fieldName]: "",
          }));
        }
        await validationSchema.validateAt(name, { [name]: value });
        setFieldErrors((prevErrors) => ({ ...prevErrors, fullAddress: "" }));
      } catch (error) {
        if (isEntity) {
          setGuarantorFieldErrors((prevErrors) => ({
            ...prevErrors,
            [fieldName]: error.message,
          }));
        } else {
          setGuarantorFieldErrors((prevErrors) => ({
            ...prevErrors,
            [fieldName]: error.message,
          }));
        }
        setFieldErrors((prevErrors) => ({
          ...prevErrors,
          fullAddress: error.message,
        }));
      }
    }, 300),
    [],
  );

  const onChangeFn = (name, newInputValue) => {
    let isValid = true;
    isValid = regex.addressRegex.test(newInputValue) && newInputValue?.length <= 200;

    if (!isValid) {
      debouncedValidation(name, newInputValue);
      return;
    }

    setInputValue(newInputValue);
    setFieldErrors({ fullAddress: "" });
  };

  const addressValue = value?._id && !value?.fullAddress ? "" : value;

  const { getCustomerOrEntityLocalAndLenderError } = useGetLocalAndLenderErrors();
  const getLocalAndLenderError = ({ fieldError, fieldName, localFieldName, doNotShowError }) =>
    getCustomerOrEntityLocalAndLenderError({
      fieldError,
      localState: {},
      fieldName,
      localFieldName,
      doNotShowError,
      isEntity,
    });

  return (
    <Autocomplete
      id={`address_${customerId}`}
      disableClearable={!canRemoveAddress}
      getOptionLabel={(option) => (typeof option === "string" ? option : option.description)}
      filterOptions={(x) => x}
      options={options}
      blurOnSelect={true}
      autoComplete
      disabled={disabled || false}
      includeInputInList
      filterSelectedOptions
      defaultValue={isEmptyAddress ? "" : value}
      value={isEmptyAddress ? "" : addressValue}
      style={style}
      onChange={async (event, newValue) => {
        if ((newValue === null || !newValue) && canRemoveAddress) {
          handleRemoveAddress(address);
          return;
        }

        if (newValue) {
          placesService.getDetails(
            {
              placeId: newValue?.place_id,
            },
            (place) => {
              if (address) {
                setOptions(newValue ? [newValue, ...options] : options);
                setValue(newValue);
                isEntity
                  ? handleUpdateAddress({
                      _id: address?._id,
                      address: place,
                      entityId: customerId,
                    })
                  : handleUpdateAddress({
                      _id: address?._id,
                      address: place,
                      customerId: customerId,
                    });
                setFieldErrors({ fullAddress: "" });
                return;
              }

              if (place) {
                setOptions(newValue ? [newValue, ...options] : options);
                setValue(newValue);
                handleCreateAddress(place);
                setFieldErrors({ fullAddress: "" });
              }
            },
          );
        } else {
          setOptions(newValue ? [newValue, ...options] : options);
          setValue(newValue);
          setFieldErrors({ fullAddress: "" });
        }
      }}
      onInputChange={(event, newInputValue) => {
        onChangeFn(addressFieldName, newInputValue);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Address"
          fullWidth
          variant="filled"
          size="small"
          name={addressFieldName}
          error={getLocalAndLenderError({
            fieldError: fieldErrors?.fullAddress,
            fieldName: requiredFieldName,
            doNotShowError: !!value,
          })}
          helperText={getLocalAndLenderError({
            fieldError: fieldErrors?.fullAddress,
            fieldName: requiredFieldName,
            doNotShowError: !!value,
          })}
          onChange={(event) => {
            debouncedValidation(addressFieldName, event?.target?.value);
            try {
              if (fieldErrors["fullAddress"] === "") {
                return;
              }
            } catch (error) {
              const newErrors = {};
              error.inner.forEach((validationError) => {
                newErrors[validationError.path] = validationError.message;
              });
              setFieldErrors(newErrors);
            }
          }}
        />
      )}
      renderOption={(props, option) => (
        <li {...props}>
          <Grid container alignItems="center">
            <Grid item>
              <Box component={LocationOnIcon} sx={{ color: "text.secondary", mr: 2 }} />
            </Grid>
            <Grid item xs>
              <Typography variant="body2" color="text.secondary">
                {option.description}
              </Typography>
            </Grid>
          </Grid>
        </li>
      )}
    />
  );
}

export default GoogleMaps;
